import { ChevronLeft } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Navigate, useLocation, useNavigate, useParams, useSearchParams } from "react-router";
import type { ClassListSnapshot } from "../api/landingClass.types";
import BenefitList from "../components/BenefitList";
import EnrollSteps from "../components/EnrollSteps";
import LandingIcon from "../components/LandingIcon";
import { LandingErrorState, LandingLoadingState } from "../components/LandingState";
import OrderSummaryCard from "../components/OrderSummaryCard";
import StickyEnrollBar from "../components/StickyEnrollBar";
import {
  LANDING_ASSETS,
  LANDING_DETAIL_TYPOGRAPHY,
  LANDING_PAGE_GUTTER,
  LANDING_TYPOGRAPHY,
} from "../constants/landing.constants";
import { LANDING_USE_MOCK } from "../config/landing.config";
import { getCourseBySlug } from "../data/landing.mock";
import { useClassView } from "../hooks/useClassView";
import type { LandingCourse } from "../types/landing.types";
import { buildClassCatalogPath } from "@/utils/catalogSlug";
import {
  loadClassDetailSnapshot,
  saveClassDetailSnapshot,
} from "../utils/classDetailSnapshot";
import {
  loadLandingListingQuery,
} from "../utils/landingListingQuery";
import {
  saveCheckoutSession,
  type CheckoutSessionState,
} from "../utils/checkoutSession";
import { resolveCourseSlugsToClass } from "../utils/catalogSlugResolve";

function MockCourseDetailPage() {
  const { categorySlug = "", courseSlug = "" } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const course = getCourseBySlug(categorySlug, courseSlug);

  const listingQuery =
    (location.state as { listingQuery?: string } | null)?.listingQuery ||
    loadLandingListingQuery(categorySlug) ||
    "";
  const categoryListingHref = listingQuery
    ? `/classes/${categorySlug}?${listingQuery}`
    : `/classes/${categorySlug}`;

  if (!course) {
    return <Navigate to={categoryListingHref} replace />;
  }

  const handleEnroll = () => {
    const checkoutState: CheckoutSessionState = {
      course,
      levelId: categorySlug,
      classId: courseSlug,
      categorySlug,
      courseSlug,
    };
    saveCheckoutSession(checkoutState);
    navigate(
      `/checkout?category=${encodeURIComponent(categorySlug)}&class=${encodeURIComponent(courseSlug)}`,
      { state: checkoutState }
    );
  };

  return (
    <CourseDetailLayout
      course={course}
      onEnroll={handleEnroll}
      onBack={() => navigate(categoryListingHref)}
    />
  );
}

function ApiCourseDetailPage() {
  const {
    categorySlug = "",
    courseSlug = "",
    levelId: legacyLevelId = "",
    classId: legacyClassId = "",
  } = useParams();
  const [searchParams] = useSearchParams();
  const level1Id = searchParams.get("level1Id") ?? "";
  const navigate = useNavigate();
  const location = useLocation();

  const [resolvedIds, setResolvedIds] = useState<{
    level2Id: string;
    level1Id: string;
    classId: string;
    categorySlug: string;
    courseSlug: string;
  } | null>(null);
  const [resolveLoading, setResolveLoading] = useState(
    !legacyLevelId && Boolean(categorySlug && courseSlug)
  );
  const [resolveFailed, setResolveFailed] = useState(false);

  useEffect(() => {
    if (legacyLevelId && legacyClassId) {
      setResolvedIds({
        level2Id: legacyLevelId,
        level1Id: level1Id,
        classId: legacyClassId,
        categorySlug: "",
        courseSlug: "",
      });
      setResolveLoading(false);
      setResolveFailed(false);
      return;
    }

    if (!categorySlug || !courseSlug) {
      setResolvedIds(null);
      setResolveFailed(true);
      setResolveLoading(false);
      return;
    }

    let cancelled = false;
    setResolveLoading(true);
    setResolveFailed(false);

    void resolveCourseSlugsToClass(
      categorySlug,
      courseSlug,
      level1Id || undefined
    ).then((resolved) => {
      if (cancelled) return;

      if (!resolved) {
        setResolvedIds(null);
        setResolveFailed(true);
        setResolveLoading(false);
        return;
      }

      setResolvedIds({
        level2Id: resolved.level2Id,
        level1Id: level1Id || resolved.level1Id,
        classId: resolved.classId,
        categorySlug: resolved.categorySlug,
        courseSlug: resolved.courseSlug,
      });
      setResolveLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [categorySlug, courseSlug, legacyLevelId, legacyClassId, level1Id]);

  const levelId = resolvedIds?.level2Id ?? legacyLevelId;
  const classId = resolvedIds?.classId ?? legacyClassId;
  const catalogSlugs = useMemo(
    () =>
      resolvedIds?.categorySlug && resolvedIds?.courseSlug
        ? {
            categorySlug: resolvedIds.categorySlug,
            courseSlug: resolvedIds.courseSlug,
          }
        : categorySlug && courseSlug
          ? { categorySlug, courseSlug }
          : undefined,
    [resolvedIds, categorySlug, courseSlug]
  );

  const snapshot = useMemo((): ClassListSnapshot | null => {
    const state = location.state as {
      snapshot?: ClassListSnapshot;
      listingQuery?: string;
    } | null;
    if (state?.snapshot) {
      return state.snapshot;
    }
    if (levelId && classId) {
      return loadClassDetailSnapshot(levelId, classId);
    }
    return null;
  }, [location.state, levelId, classId]);

  useEffect(() => {
    const state = location.state as { snapshot?: ClassListSnapshot } | null;
    if (state?.snapshot && levelId && classId) {
      saveClassDetailSnapshot(levelId, classId, state.snapshot);
    }
  }, [location.state, levelId, classId]);

  const { course, isLoading, error } = useClassView({
    classId: resolveLoading ? "" : classId,
    levelId: resolveLoading ? "" : levelId,
    level1Id: (resolvedIds?.level1Id || level1Id) || undefined,
    snapshot,
    catalogSlugs,
  });

  if (resolveLoading || isLoading) {
    return <LandingLoadingState message="Loading class details…" />;
  }

  if (resolveFailed || !levelId || !classId) {
    return <Navigate to="/classes" replace />;
  }

  const listingScopeId = catalogSlugs?.categorySlug || levelId;
  const listingQuery =
    (location.state as { listingQuery?: string } | null)?.listingQuery ||
    loadLandingListingQuery(listingScopeId) ||
    "";
  const effectiveParentLevel1Id =
    (resolvedIds?.level1Id || level1Id).trim() || "";
  const levelListingHref = catalogSlugs?.categorySlug
    ? listingQuery
      ? `${buildClassCatalogPath(catalogSlugs.categorySlug)}?${listingQuery}`
      : effectiveParentLevel1Id
        ? `${buildClassCatalogPath(catalogSlugs.categorySlug)}?level1Id=${encodeURIComponent(effectiveParentLevel1Id)}`
        : buildClassCatalogPath(catalogSlugs.categorySlug)
    : listingQuery
      ? `/classes/level/${levelId}?${listingQuery}`
      : effectiveParentLevel1Id
        ? `/classes/level/${levelId}?level1Id=${encodeURIComponent(effectiveParentLevel1Id)}`
        : `/classes/level/${levelId}`;

  if (error || !course) {
    return (
      <>
        <LandingErrorState message={error ?? "Class not found."} />
        <div className={`text-center ${LANDING_PAGE_GUTTER}`}>
          <button
            type="button"
            onClick={() => navigate(levelListingHref)}
            className="text-[14px] font-semibold text-landing-button hover:underline"
          >
            Back to classes
          </button>
        </div>
      </>
    );
  }

  const handleEnroll = () => {
    const checkoutState: CheckoutSessionState = {
      course,
      levelId,
      classId,
      level1Id: (resolvedIds?.level1Id || level1Id) || undefined,
      categorySlug: catalogSlugs?.categorySlug ?? course.categorySlug,
      courseSlug: catalogSlugs?.courseSlug ?? course.slug,
    };
    saveCheckoutSession(checkoutState);
    if (checkoutState.categorySlug && checkoutState.courseSlug) {
      navigate(
        `/checkout?category=${encodeURIComponent(checkoutState.categorySlug)}&class=${encodeURIComponent(checkoutState.courseSlug)}`,
        { state: checkoutState }
      );
      return;
    }

    const checkoutQuery = new URLSearchParams({
      levelId,
      classId,
    });
    if (checkoutState.level1Id) {
      checkoutQuery.set("level1Id", checkoutState.level1Id);
    }
    navigate(`/checkout?${checkoutQuery.toString()}`, { state: checkoutState });
  };

  return (
    <CourseDetailLayout
      course={course}
      onEnroll={handleEnroll}
      onBack={() => navigate(levelListingHref)}
    />
  );
}

interface CourseDetailLayoutProps {
  course: LandingCourse;
  onEnroll: () => void;
  onBack?: () => void;
}

function CourseDetailLayout({ course, onEnroll, onBack }: CourseDetailLayoutProps) {
  return (
    <>
      <section className="relative h-[236px] max-h-[236px] overflow-hidden bg-landing-navy">
        <img
          src={LANDING_ASSETS.notesPattern}
          alt=""
          className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-50"
          aria-hidden
        />

        <div
          className={`relative z-10 flex h-full max-h-[236px] flex-col justify-center py-6 ${LANDING_PAGE_GUTTER}`}
        >
          {onBack ? (
            <button
              type="button"
              onClick={onBack}
              className="mb-3 flex w-fit items-center gap-1 text-[14px] font-medium text-white/90 transition-opacity hover:text-white"
            >
              <ChevronLeft className="h-5 w-5" />
              Back
            </button>
          ) : null}

          <div className={LANDING_DETAIL_TYPOGRAPHY.heroHeader}>
            <span className={LANDING_DETAIL_TYPOGRAPHY.heroBadge} title="Class">
              Class
            </span>
            <h1 className={LANDING_DETAIL_TYPOGRAPHY.heroTitle} title={course.title}>
              {course.title}
            </h1>
          </div>
          <div className={LANDING_DETAIL_TYPOGRAPHY.heroMeta}>
            <span className={LANDING_DETAIL_TYPOGRAPHY.heroMetaItem}>
              <LandingIcon
                name="bookOpenHero"
                className={LANDING_DETAIL_TYPOGRAPHY.heroMetaIcon}
              />
              {course.units} Units
            </span>
            <span className={LANDING_DETAIL_TYPOGRAPHY.heroMetaItem}>
              <LandingIcon
                name="playHero"
                className={LANDING_DETAIL_TYPOGRAPHY.heroMetaIcon}
              />
              {course.videos} videos
            </span>
          </div>
        </div>
      </section>

      <div className={`${LANDING_PAGE_GUTTER} md:pt-10`}>
        <div className="relative grid gap-8 md:grid-cols-[minmax(0,1fr)_375px] md:items-start md:gap-10">
          <div className="order-2 space-y-10 pb-24 md:order-1 md:pb-12">
            <section className={LANDING_DETAIL_TYPOGRAPHY.aboutSection}>
              <h2
                className={`mb-4 -mx-[12px] px-[12px] ${LANDING_TYPOGRAPHY.subsectionTitle}`}
              >
                About The Class
              </h2>
              <p className={LANDING_DETAIL_TYPOGRAPHY.aboutBody}>
                {course.longDescription}
              </p>
            </section>

            <BenefitList benefits={course.benefits} />
            <EnrollSteps steps={course.enrollSteps} />
          </div>

          <div className="relative z-20 order-1 self-start md:order-2 md:-mt-[170px] md:sticky md:top-[var(--landing-sticky-top)] md:z-30">
            <OrderSummaryCard
              course={course}
              showEnrollButton
              onEnroll={onEnroll}
              className="mx-auto -mt-10 md:mx-0 md:mt-0"
            />
          </div>
        </div>
      </div>

      <StickyEnrollBar course={course} onEnroll={onEnroll} />
    </>
  );
}

export default function CourseDetailPage() {
  return LANDING_USE_MOCK ? <MockCourseDetailPage /> : <ApiCourseDetailPage />;
}
